import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import './index.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AchievementProvider, useAchievement } from './contexts/AchievementContext'
import { SettingsProvider } from './contexts/SettingsContext'
import { playSound } from './lib/sounds'
import AuthPage from './pages/AuthPage'
import Sidebar from './components/Sidebar'
import NoCompanyScreen from './components/NoCompanyScreen'
import PendingApprovalScreen from './components/PendingApprovalScreen'
import ErrorBoundary from './components/ErrorBoundary'
import ResponsiveDrawer from './components/ui/ResponsiveDrawer'
import { triggerConfetti } from './lib/confetti'
import { kanbify } from './lib/kanbifyClient'
import { appRoutes, buildSearch, isKnownAppPath } from './lib/routes'
import { identifyProductUser, resetProductAnalytics } from './lib/productAnalytics'
import { useCards } from './hooks/useKanbifyData'
import { useCompany } from './hooks/useCompanyData'
import { useProcedures } from './hooks/useProcedures'
import { useHeartbeat } from './hooks/useHeartbeat'
import AppPageHeader from './components/app/AppPageHeader'
import GlobalSearch from './components/GlobalSearch'
import CardDetail from './components/CardDetail'
import { LayoutDashboard, Clock, User, LogOut, CalendarClock, CalendarDays, Zap } from 'lucide-react'

const Leaderboard = lazy(() => import('./components/Leaderboard'))
const ProfilePage = lazy(() => import('./components/ProfilePage'))
const CompanyPage = lazy(() => import('./components/CompanyPage'))
const ProceduresView = lazy(() => import('./components/ProceduresView'))
const HistoryView = lazy(() => import('./components/HistoryView'))
const SchedulerView = lazy(() => import('./components/SchedulerView'))
const CalendarView = lazy(() => import('./components/CalendarView'))
const DashboardPersonalView = lazy(() => import('./components/DashboardPersonalView'))
const SuggestionsPage = lazy(() => import('./components/SuggestionsPage'))
const CockpitPage = lazy(() => import('./components/CockpitPage'))
const ArchiveView = lazy(() => import('./components/ArchiveView'))
const SpectatorBoard = lazy(() => import('./components/SpectatorBoard'))

const CARD_DETAIL_SELECT = `
    *,
    card_labels ( label_id, labels:label_id ( id, name, color ) ),
    card_members ( user_id, claimed_at, profiles:user_id ( id, full_name, avatar_url ) ),
    checklists ( id, name, checklist_items ( id, is_completed, is_shared ) ),
    comments ( id ),
    card_completions ( id, user_id, completed_at )
`

function LoadingFallback({ label, overlay = false }) {
    const className = overlay ? 'modal-loading' : 'panel-loading'
    return (
        <div className={className}>
            <span>{label}</span>
        </div>
    )
}

function AppContent() {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { user, profile, isAdmin, canCreate, loading: authLoading, refreshProfile, sessionExpired, dismissSessionExpired } = useAuth()
    const { showAchievement } = useAchievement()

    // Heartbeat — admin cockpit foloseste user_heartbeats.last_seen_at pentru online status
    useHeartbeat()

    const [activeBoardId, setActiveBoardId] = useState(null)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [showGlobalSearch, setShowGlobalSearch] = useState(false)
    // Spectator mode: { id, name } of the employee whose board we are watching read-only.
    const [spectatorUser, setSpectatorUser] = useState(null)
    const [selectedCardFallback, setSelectedCardFallback] = useState(null)
    const [selectedCardLoading, setSelectedCardLoading] = useState(false)
    const openCardSeqRef = useRef(0)
    const boardWrapperRef = useRef(null)
    const scrollPositionsRef = useRef(new Map())

    // Cmd+K toggles global search
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setShowGlobalSearch((prev) => !prev)
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Toast on session expiry
    useEffect(() => {
        if (sessionExpired) {
            showAchievement({ type: 'error', message: 'Sesiunea a expirat. Te rugăm să te loghezi din nou.' })
            dismissSessionExpired()
        }
    }, [sessionExpired, showAchievement, dismissSessionExpired])

    // Product analytics identification
    useEffect(() => {
        if (user?.id) identifyProductUser(user, profile)
        else resetProductAnalytics()
    }, [profile, user])

    // Data needed by CardDetail when ?card= is in URL.
    // We hydrate the board context for whichever board owns the selected card.
    const { cards, loading: cardsLoading, update: updateCard, remove: removeCard, fetch: fetchCards } = useCards(activeBoardId)
    // Procedures are needed only when CardDetail is open (linker UI).
    // The /procedures page owns its own data; keeping this hook inactive
    // there prevents a duplicate shim poll from causing tiny visual refreshes.
    const proceduresActive = Boolean(searchParams.get('card'))
    const { procedures } = useProcedures(proceduresActive ? profile?.company_id : null)
    const { company, members: companyMembers } = useCompany(profile?.company_id)
    const dashboardV2Enabled = Boolean(company?.dashboard_v2_enabled)

    const pathname = location.pathname
    const showHome = pathname === appRoutes.home
    const showLeaderboard = pathname === appRoutes.leaderboard
    const showProcedures = pathname === appRoutes.procedures
    const showHistory = pathname === appRoutes.history
    const showScheduler = pathname === appRoutes.scheduler
    const showCalendar = pathname === appRoutes.calendar
    const showSuggestions = pathname === appRoutes.suggestions
    const showCockpit = pathname === appRoutes.cockpit
    const showArchive = pathname === appRoutes.archive
    const showProfile = searchParams.get('modal') === 'profile'
    const showCompany = searchParams.get('modal') === 'company'
    const selectedCardId = searchParams.get('card')
    // `ro=1` in the URL → the card was opened from spectator mode: render it read-only.
    const selectedCardReadOnly = searchParams.get('ro') === '1'
    // Only admins/managers (and super-admins, folded into isAdmin) may watch an employee's board.
    const canSpectate = isAdmin || profile?.role === 'manager' || profile?.role === 'admin'

    useEffect(() => {
        const wrapper = boardWrapperRef.current
        if (!wrapper) return undefined
        const scrollPositions = scrollPositionsRef.current
        const key = pathname
        let frame = null
        const savedTop = scrollPositions.get(key)
        if (typeof savedTop === 'number') {
            frame = requestAnimationFrame(() => {
                wrapper.scrollTop = savedTop
            })
        }
        const handleScroll = () => {
            scrollPositions.set(key, wrapper.scrollTop)
        }
        wrapper.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            if (frame) cancelAnimationFrame(frame)
            scrollPositions.set(key, wrapper.scrollTop)
            wrapper.removeEventListener('scroll', handleScroll)
        }
    }, [pathname])

    /* ---------- Routing guards ---------- */

    useEffect(() => {
        if (authLoading || !user) return

        if (profile && !profile.company_id) {
            if (pathname !== appRoutes.onboarding) {
                navigate({ pathname: appRoutes.onboarding, search: location.search }, { replace: true })
            }
            return
        }

        if (profile?.company_id) {
            // Default landing → /home (Dashboard Personal)
            if (pathname === '/' || pathname === appRoutes.onboarding) {
                navigate({ pathname: appRoutes.home, search: location.search }, { replace: true })
                return
            }
            // Any unknown path (including legacy /boards/X, /my-tasks, /dashboard) → /home
            if (!isKnownAppPath(pathname)) {
                navigate({ pathname: appRoutes.home, search: location.search }, { replace: true })
            }
        }
    }, [authLoading, dashboardV2Enabled, location.search, navigate, pathname, profile, user])

    /* ---------- Search params helpers ---------- */

    const updateSearchState = useCallback((updates = {}, removals = [], options = {}) => {
        const nextSearch = buildSearch(searchParams, updates, removals)
        const currentSearch = searchParams.toString()
        const normalizedCurrent = currentSearch ? `?${currentSearch}` : ''
        if (nextSearch === normalizedCurrent) return
        setSearchParams(nextSearch, options)
    }, [searchParams, setSearchParams])

    const closeSidebar = useCallback(() => setIsSidebarOpen(false), [])
    const closeCardRoute = useCallback(() => {
        setSelectedCardFallback(null)
        setSelectedCardLoading(false)
        updateSearchState({}, ['card', 'ro'], { replace: true })
    }, [updateSearchState])
    const openModal = (modal) => updateSearchState({ modal }, ['card'], { replace: true })
    const closeModal = (modal) => {
        if (searchParams.get('modal') !== modal) return
        updateSearchState({}, ['modal'], { replace: true })
    }

    /* ---------- Card opening (modal-only, no board navigation) ---------- */

    const resolveBoardIdForCard = useCallback(async (cardId) => {
        const { data: card } = await kanbify
            .from('cards')
            .select('list_id')
            .eq('id', cardId)
            .maybeSingle()
        if (!card?.list_id) return null
        const { data: list } = await kanbify
            .from('lists')
            .select('board_id')
            .eq('id', card.list_id)
            .maybeSingle()
        return list?.board_id || null
    }, [])

    const fetchCardForModal = useCallback(async (cardId) => {
        if (!cardId) return null
        const { data, error } = await kanbify
            .from('cards')
            .select(CARD_DETAIL_SELECT)
            .eq('id', cardId)
            .maybeSingle()
        if (error) return null
        return data || null
    }, [])

    const openCardRoute = useCallback(async (cardId, options = {}) => {
        if (!cardId) return
        if (
            selectedCardId === cardId
            && !selectedCardLoading
            && (selectedCardFallback?.id === cardId || cards.some((card) => card.id === cardId))
        ) return
        const normalizedOptions = typeof options === 'string' ? { boardId: options } : (options || {})
        const seq = ++openCardSeqRef.current
        let existingCard = normalizedOptions.card || cards.find((card) => card.id === cardId) || null
        const shouldPreloadBeforeOpen = Boolean(normalizedOptions.preload)

        if (shouldPreloadBeforeOpen) {
            // Open the modal IMMEDIATELY (loading state) for instant feedback, then
            // resolve board + card in the background and fill it in. Awaiting both
            // fetches BEFORE opening made the click feel dead on slower links (cPanel
            // shim) — users clicked several times and waited for the card to appear.
            // (Teo, 2026-05-22 — feedback #2 „intra greu / trebuie mai multe click-uri".)
            setSelectedCardLoading(!existingCard)
            if (existingCard) setSelectedCardFallback(existingCard)
            updateSearchState({ card: cardId, ro: normalizedOptions.readOnly ? '1' : null }, ['modal'], { replace: normalizedOptions.replace ?? false })
            closeSidebar()

            const [targetBoardId, preloadedCard] = await Promise.all([
                normalizedOptions.boardId ? Promise.resolve(normalizedOptions.boardId) : resolveBoardIdForCard(cardId),
                existingCard ? Promise.resolve(existingCard) : fetchCardForModal(cardId),
            ])
            if (seq !== openCardSeqRef.current) return
            if (preloadedCard) {
                setSelectedCardFallback(preloadedCard)
            } else if (!targetBoardId) {
                setSelectedCardLoading(false)
                showAchievement({ type: 'error', message: 'Cardul nu a mai putut fi găsit.' })
                return
            }
            if (targetBoardId && activeBoardId !== targetBoardId) {
                setActiveBoardId(targetBoardId)
            }
            setSelectedCardLoading(false)
            return
        }

        setSelectedCardLoading(!existingCard)
        if (existingCard) setSelectedCardFallback(existingCard)
        updateSearchState({ card: cardId, ro: normalizedOptions.readOnly ? '1' : null }, ['modal'], { replace: normalizedOptions.replace ?? false })
        closeSidebar()

        const [targetBoardId, directCard] = await Promise.all([
            normalizedOptions.boardId ? Promise.resolve(normalizedOptions.boardId) : resolveBoardIdForCard(cardId),
            existingCard ? Promise.resolve(existingCard) : fetchCardForModal(cardId),
        ])

        if (seq !== openCardSeqRef.current) return
        if (directCard) {
            setSelectedCardFallback((current) => (
                current?.id === directCard.id && JSON.stringify(current) === JSON.stringify(directCard)
                    ? current
                    : directCard
            ))
        }
        if (!targetBoardId && !directCard) {
            setSelectedCardLoading(false)
            showAchievement({ type: 'error', message: 'Cardul nu a mai putut fi găsit.' })
            return
        }
        if (targetBoardId && activeBoardId !== targetBoardId) {
            setActiveBoardId(targetBoardId)
        }
        setSelectedCardLoading(false)
    }, [activeBoardId, cards, closeSidebar, fetchCardForModal, resolveBoardIdForCard, selectedCardFallback?.id, selectedCardId, selectedCardLoading, showAchievement, updateSearchState])

    // When landing on /home?card=X (e.g. reload), make sure the card's board
    // is loaded so CardDetail has data.
    useEffect(() => {
        if (!selectedCardId) return undefined
        if (!profile?.company_id) return undefined
        let cancelled = false
        ;(async () => {
            const hasCurrentFallback = selectedCardFallback?.id === selectedCardId
            const hasCurrentBoardCard = cards.some((card) => card.id === selectedCardId)
            const [resolvedBoardId, directCard] = await Promise.all([
                resolveBoardIdForCard(selectedCardId),
                hasCurrentFallback || hasCurrentBoardCard ? Promise.resolve(null) : fetchCardForModal(selectedCardId),
            ])
            if (cancelled) return
            if (directCard) setSelectedCardFallback(directCard)
            if (resolvedBoardId) {
                setActiveBoardId((current) => current === resolvedBoardId ? current : resolvedBoardId)
            }
            setSelectedCardLoading(false)
        })()
        return () => { cancelled = true }
    }, [cards, fetchCardForModal, profile?.company_id, resolveBoardIdForCard, selectedCardFallback?.id, selectedCardId])

    /* ---------- Derived ---------- */

    const selectedCardData = useMemo(() => {
        if (!selectedCardId) return null
        // Prefer the LIVE card from the polled list so the open modal reflects
        // updates (timer started elsewhere / auto-chain, completion, etc.) without
        // a manual refresh. The frozen snapshot is only a fallback for cards not
        // yet in the list (opened from search/deep-link). (Teo 2026-06-04)
        return cards.find((card) => card.id === selectedCardId)
            || (selectedCardFallback?.id === selectedCardId ? selectedCardFallback : null)
            || null
    }, [cards, selectedCardFallback, selectedCardId])

    const cardDetailAllCards = useMemo(() => {
        if (!selectedCardFallback || cards.some((card) => card.id === selectedCardFallback.id)) return cards
        return [...cards, selectedCardFallback]
    }, [cards, selectedCardFallback])

    /* ---------- Card mutation handlers ---------- */

    const handleUpdateCard = async (cardId, updates) => {
        const { card: previousCard, error: prevError } = await (async () => {
            const { data, error } = await kanbify.from('cards').select('*').eq('id', cardId).maybeSingle()
            return { card: data, error }
        })()
        if (prevError) showAchievement({ type: 'error', message: 'Nu am putut citi cardul' })

        const result = await updateCard(cardId, updates)
        if (result?.success === false) {
            showAchievement({ type: 'error', message: result.error })
            return
        }
        setSelectedCardFallback((current) => (
            current?.id === cardId ? { ...current, ...updates } : current
        ))

        // Points are now awarded by DB trigger fn_award_card_completion based on
        // cards.point_value (set explicitly or inherited from procedure). No more
        // flat 10p from client. Trigger handles all assignees for solo cards and
        // per-user for shared cards. See migration 20260512140300_*.
        if (updates.is_completed === true && previousCard && !previousCard.is_completed) {
            triggerConfetti()
            playSound('complete')
            // Brief delay to let the trigger commit before refetching the profile.
            setTimeout(() => refreshProfile?.(), 300)
        }
    }

    // KPI sidebar refresh (Teo 2026-05-26): dashboard's KPI modal writes
    // completion_payload directly via kanbify.update; CardDetail (if open)
    // would stay stale until next 15s realtime poll. Listen for the patch
    // event and merge into selectedCardFallback so the right sidebar reflects
    // the new KPI values immediately after submit.
    useEffect(() => {
        const handler = (e) => {
            const { cardId, patch } = e?.detail || {}
            if (!cardId || !patch) return
            setSelectedCardFallback((current) => (
                current?.id === cardId ? { ...current, ...patch } : current
            ))
        }
        window.addEventListener('kanbify:card-payload-patch', handler)
        return () => window.removeEventListener('kanbify:card-payload-patch', handler)
    }, [])

    const handleDeleteCard = async (cardId) => {
        const result = await removeCard(cardId)
        if (result?.success === false) {
            showAchievement({ type: 'error', message: result.error })
            return
        }
        // Live update: drop the archived card from the dashboard instantly
        // (optimistic) + trigger a refetch so every open view stays in sync
        // without a manual refresh.
        window.dispatchEvent(new CustomEvent('kanbify:dashboard-card-state', { detail: { type: 'removed', cardId } }))
        window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        closeCardRoute()
        setSelectedCardFallback(null)
    }

    /* ---------- Navigation handlers (sidebar items) ---------- */

    const openHome = () => { navigate({ pathname: appRoutes.home }); closeSidebar() }
    const openLeaderboard = () => { navigate({ pathname: appRoutes.leaderboard }); closeSidebar() }
    const openProcedures = () => { navigate({ pathname: appRoutes.procedures }); closeSidebar() }
    const openHistory = () => { navigate({ pathname: appRoutes.history }); closeSidebar() }
    const openScheduler = () => { navigate({ pathname: appRoutes.scheduler }); closeSidebar() }
    const openCalendar = () => { navigate({ pathname: appRoutes.calendar }); closeSidebar() }
    const openSuggestions = () => { navigate({ pathname: appRoutes.suggestions }); closeSidebar() }
    const openCockpit = () => { navigate({ pathname: appRoutes.cockpit }); closeSidebar() }
    const openArchive = () => { navigate({ pathname: appRoutes.archive }); closeSidebar() }
    const openProfile = () => { openModal('profile'); closeSidebar() }
    const openCompany = () => { openModal('company'); closeSidebar() }

    /* ---------- Render ---------- */

    const renderShellSidebar = (mobile = false) => (
        <Sidebar
            onShowLeaderboard={openLeaderboard}
            showLeaderboard={showLeaderboard}
            onShowProcedures={openProcedures}
            showProcedures={showProcedures}
            onShowHistory={openHistory}
            showHistory={showHistory}
            onShowScheduler={openScheduler}
            showScheduler={showScheduler}
            onShowCalendar={openCalendar}
            showCalendar={showCalendar}
            onShowHome={profile?.company_id ? openHome : undefined}
            showHome={showHome}
            onShowSuggestions={openSuggestions}
            showSuggestions={showSuggestions}
            onShowCockpit={profile?.company_id ? openCockpit : undefined}
            showCockpit={showCockpit}
            onShowArchive={profile?.company_id ? openArchive : undefined}
            showArchive={showArchive}
            dashboardV2Enabled={dashboardV2Enabled}
            onShowProfile={openProfile}
            onShowCompany={profile?.company_id ? openCompany : undefined}
            onCloseMobile={mobile ? closeSidebar : undefined}
        />
    )

    if (authLoading) {
        return (
            <div className="auth-page">
                <div className="auth-bg" />
                <LoadingFallback label="Se incarca..." />
            </div>
        )
    }

    if (!user) return <AuthPage />

    if (user && profile && profile.is_active === false) {
        return <PendingApprovalScreen />
    }

    if (user && profile && !profile.company_id) {
        return (
            <div className="app-shell">
                <ResponsiveDrawer open={isSidebarOpen} onClose={closeSidebar}>
                    {isSidebarOpen ? renderShellSidebar(true) : null}
                </ResponsiveDrawer>
                <div className="app-sidebar-desktop">{renderShellSidebar(false)}</div>
                <div className="main-content">
                    <NoCompanyScreen profile={profile} onJoined={refreshProfile} />
                </div>
            </div>
        )
    }

    return (
        <ErrorBoundary>
            <div className="app-shell">
                <ResponsiveDrawer open={isSidebarOpen} onClose={closeSidebar}>
                    {isSidebarOpen ? renderShellSidebar(true) : null}
                </ResponsiveDrawer>

                <div className="app-sidebar-desktop">
                    {renderShellSidebar(false)}
                </div>

                <div className="main-content">
                    <AppPageHeader
                        showLeaderboard={showLeaderboard}
                        showProcedures={showProcedures}
                        showHistory={showHistory}
                        showScheduler={showScheduler}
                        showHome={showHome}
                        showSuggestions={showSuggestions}
                        onOpenSidebar={() => setIsSidebarOpen(true)}
                        onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                        onOpenGlobalSearch={() => setShowGlobalSearch(true)}
                    />

                    <div className="board-wrapper" ref={boardWrapperRef}>
	                        {showHome ? (
	                            <Suspense fallback={<LoadingFallback label="Se incarca dashboard-ul personal..." />}>
	                                <DashboardPersonalView onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })} />
	                            </Suspense>
                        ) : showSuggestions ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca sugestiile..." />}>
                                <SuggestionsPage />
                            </Suspense>
                        ) : showCockpit ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca cockpit-ul..." />}>
                                <CockpitPage
                                    onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                                    onSpectate={canSpectate ? (u) => setSpectatorUser(u) : undefined}
                                />
                            </Suspense>
                        ) : showArchive ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca arhiva..." />}>
                                <ArchiveView onOpenCard={(cardId) => openCardRoute(cardId, { preload: true, readOnly: true })} />
                            </Suspense>
                        ) : showCalendar ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca calendarul..." />}>
                                <CalendarView onOpenCard={(cardId) => openCardRoute(cardId, { preload: true })} />
                            </Suspense>
                        ) : showHistory ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca istoricul..." />}>
                                <HistoryView
                                    companyId={profile?.company_id}
                                    companyMembers={companyMembers}
                                    onOpenCard={openCardRoute}
                                />
                            </Suspense>
                        ) : showScheduler ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca programarea..." />}>
                                <SchedulerView
                                    companyId={profile?.company_id}
                                    onOpenCard={(cardId, boardId) => openCardRoute(cardId, { preload: true, boardId })}
                                />
                            </Suspense>
                        ) : showProcedures ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca procedurile..." />}>
                                <ProceduresView />
                            </Suspense>
                        ) : showLeaderboard ? (
                            <Suspense fallback={<LoadingFallback label="Se incarca clasamentul..." />}>
                                <Leaderboard />
                            </Suspense>
                        ) : (
                            <LoadingFallback label="Se incarca..." />
                        )}
                    </div>
                </div>

                {/* Card detail modal — overlays whatever view is active */}
                {selectedCardId && !selectedCardData && (cardsLoading || selectedCardLoading) ? (
                    <LoadingFallback label="Se incarca detaliile..." overlay />
                ) : null}

                {selectedCardData ? (
                    <CardDetail
                        card={selectedCardData}
                        allCards={cardDetailAllCards}
                        onClose={closeCardRoute}
                        onUpdate={handleUpdateCard}
                        onDelete={handleDeleteCard}
                        onRefreshCards={fetchCards}
                        isAdmin={isAdmin}
                        canCreate={canCreate}
                        procedures={procedures}
                        readOnly={selectedCardReadOnly}
                    />
                ) : null}

                {/* Spectator board — read-only view of an employee's day (CEO oversight) */}
                {spectatorUser ? (
                    <Suspense fallback={<LoadingFallback label="Se incarca board-ul angajatului..." overlay />}>
                        <SpectatorBoard
                            userId={spectatorUser.id}
                            userName={spectatorUser.name}
                            companyId={profile?.company_id}
                            onClose={() => setSpectatorUser(null)}
                            onOpenCard={(cardId) => openCardRoute(cardId, { preload: true, readOnly: true })}
                        />
                    </Suspense>
                ) : null}

                {/* Profile / Company modals (?modal= param) */}
                {showProfile && profile ? (
                    <Suspense fallback={<LoadingFallback label="Se incarca profilul..." overlay />}>
                        <ProfilePage onClose={() => closeModal('profile')} />
                    </Suspense>
                ) : null}

                {showCompany && profile?.company_id ? (
                    <Suspense fallback={<LoadingFallback label="Se incarca compania..." overlay />}>
                        <CompanyPage
                            onClose={() => closeModal('company')}
                            onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                        />
                    </Suspense>
                ) : null}

                {/* Cmd+K global search */}
                <GlobalSearch
                    companyId={profile?.company_id}
                    isOpen={showGlobalSearch}
                    onClose={() => setShowGlobalSearch(false)}
                    onSelectCard={(cardId) => openCardRoute(cardId, { preload: true })}
                    onSelectBoard={() => { /* boards no longer navigable as a view */ }}
                    onSelectSuggestion={() => { navigate({ pathname: appRoutes.suggestions }) }}
                    onSelectMember={canSpectate ? (member) => { setSpectatorUser({ id: member.id, name: member.full_name || member.email }); setShowGlobalSearch(false) } : undefined}
                    actions={[
                        ...(profile?.company_id ? [{
                            id: 'open-home',
                            label: 'Acasă (Dashboard Personal)',
                            icon: LayoutDashboard,
                            run: openHome,
                        }] : []),
                        { id: 'open-suggestions', label: 'Deschide Sugestii', icon: Zap, run: openSuggestions },
                        { id: 'open-history', label: 'Deschide Istoric', icon: Clock, run: openHistory },
                        { id: 'open-scheduler', label: 'Deschide Programare', icon: CalendarClock, run: openScheduler },
                        { id: 'open-calendar', label: 'Deschide Calendar', icon: CalendarDays, run: openCalendar },
                        { id: 'open-profile', label: 'Deschide profilul', icon: User, run: openProfile },
                        {
                            id: 'sign-out',
                            label: 'Deconectare',
                            icon: LogOut,
                            run: async () => {
                                const { error } = await kanbify.auth.signOut()
                                if (error) showAchievement({ type: 'error', message: error.message })
                            },
                        },
                    ]}
                />

            </div>
        </ErrorBoundary>
    )
}

export default function App() {
    return (
        <SettingsProvider>
            <AchievementProvider>
                <AuthProvider>
                    <AppContent />
                </AuthProvider>
            </AchievementProvider>
        </SettingsProvider>
    )
}
